import { z } from 'zod';
import { BallColorSchema } from "./balls.js";
import { PlayerIndexSchema } from "./events.js";
/**
 * The wire between the recogniser (`super_snooker`) and the scoreboard.
 *
 * The recogniser sees balls; it does not know the rules, whose turn it is or
 * what the score was. So it never sends an event — it sends a *candidate*: an
 * observation, with the pictures it was drawn from, that a human then accepts
 * or rejects. Only the scoreboard turns an accepted candidate into a
 * `MatchEvent`, and only the event log moves the score.
 *
 * Three consequences run through everything below:
 *
 *  1. A candidate carries no `player`, no points and no `seq`. The recogniser
 *     cannot know them, and a field it has to invent is a field that will
 *     eventually be wrong.
 *  2. The wire is snake_case, because the producer is Python and dumps its
 *     dataclasses straight out. Renaming the keys would buy nothing and cost a
 *     translation layer on the side that can least afford a bug. The schemas
 *     parse snake_case and hand back camelCase, so only this file knows.
 *  3. Every message is versioned. The two projects are separate repositories
 *     on separate release cycles; a silent shape change would show up as a
 *     mis-scored frame rather than an error.
 */
/**
 * Bump when a change would make an older worker's messages mean something
 * different. Adding an optional field does not count; changing what a `kind`
 * implies does.
 */
export const AI_PROTOCOL_VERSION = 1;
/**
 * What the recogniser believes it saw.
 *
 * These six exist because they are what a camera can honestly report. Three
 * of them are not scoring events at all — a ball off the table waiting to be
 * replaced is a fact about the table, not about the match — and keeping them
 * in the same channel is what stops the pipeline from inventing a pot to
 * explain a ball that simply went missing for a while.
 *
 * `turn_ended_no_score` is the one to distrust: it is also what an occluded
 * pot looks like. It is a suggestion to hand over, never a fact.
 */
export const AiCandidateKindSchema = z.enum([
    /** Balls of one colour left the table and the shot was legal-looking. */
    'ball_potted',
    /** The cue ball went in. A foul, but the recogniser cannot price it. */
    'cue_ball_potted_foul',
    /** A colour (or the cue ball) is off the table and owed a spot. */
    'ball_waiting_for_spot',
    /** It came back. Table state only — this must never touch the score. */
    'ball_respotted',
    /** Balls moved, nothing left the table: probably a miss. */
    'turn_ended_no_score',
    /** Something happened and the pipeline will not guess. Always human-only. */
    'uncertain',
]);
/**
 * The cue ball is not a `BallColor`: it is never a pot and never scores, so it
 * is deliberately outside the scoring enum and only legal on the kinds that
 * describe it leaving and returning.
 */
export const AiCandidateColorSchema = z.union([BallColorSchema, z.literal('white')]);
/**
 * The smallest foul in snooker, and the default the scoreboard offers when the
 * cue ball goes in.
 *
 * It is a range rather than the constant four because the recogniser already
 * knows better than four: if the blue was potted in the same shot the foul is
 * five. Pinning this at four would throw away something the pipeline computed
 * and force the operator to re-enter it by hand. Seven is the ceiling — the
 * black — and the operator can always overrule.
 */
export const MIN_FOUL_POINTS = 4;
export const MAX_FOUL_POINTS = 7;
/**
 * Why the recogniser thinks so.
 *
 * Counts before and after are the whole argument for a pot, and they are small
 * enough to travel and to store. Frames are *identifiers*, not pictures:
 * images live in the run directory on the worker and arrive, if ever, as a
 * URI. A protocol that can carry a JPEG is a protocol that will one day carry
 * a thousand of them into a database.
 */
export const AiEvidenceSchema = z
    .object({
    before_counts: z.record(z.string(), z.number().int().nonnegative()).default({}),
    after_counts: z.record(z.string(), z.number().int().nonnegative()).default({}),
    source_frame_ids: z.array(z.number().int().nonnegative()).max(64).default([]),
    /** The pipeline's own event id, so a candidate can be traced back. */
    source_event_id: z.string().max(200).nullish(),
    /** The pipeline's internal verdict, e.g. `accepted`, `needs_review`. */
    decision: z.string().max(80).nullish(),
    /** Why, e.g. `valid_pot_with_pocket_track`. */
    reason: z.string().max(200).nullish(),
    /** The moving segment the observation was drawn from. */
    moving_segment_id: z.string().max(200).nullish(),
    /** Where a human can go and look. Never the image itself. */
    debug_uri: z.string().max(500).nullish(),
})
    .transform((e) => ({
    beforeCounts: e.before_counts,
    afterCounts: e.after_counts,
    sourceFrameIds: e.source_frame_ids,
    sourceEventId: e.source_event_id ?? null,
    decision: e.decision ?? null,
    reason: e.reason ?? null,
    movingSegmentId: e.moving_segment_id ?? null,
    debugUri: e.debug_uri ?? null,
}));
const candidateWire = z.object({
    /**
     * Deterministic: `runId:eventGroupId:kind:ordinal`. The same observation
     * re-sent after a timeout must produce the same id, because that id is what
     * makes a retry free. It becomes the event's `clientId` on acceptance, so
     * the idempotency reaches all the way into the log.
     */
    candidate_id: z.string().min(1).max(300),
    /** One worker process with one model. A restart mints a new one. */
    run_id: z.string().min(1).max(200),
    /** The table the camera is pointed at. See the note on `AI_API`. */
    table_id: z.string().min(1).max(200),
    stream_id: z.string().min(1).max(200),
    segment_id: z.string().min(1).max(200),
    /**
     * One shot. A single stroke can produce several candidates — the cue ball
     * fouls *and* is owed a spot — and they have to be reviewed together, or the
     * operator accepts half a shot.
     */
    event_group_id: z.string().min(1).max(200),
    kind: AiCandidateKindSchema,
    color: AiCandidateColorSchema.nullish(),
    count: z.number().int().min(1).max(15).nullish(),
    confidence: z.number().min(0).max(1),
    /**
     * Position in the *stream*, not the wall clock: the worker may be replaying
     * a file, and it has no clock worth trusting anyway. It is only comparable
     * within a `run_id`; the run's `started_at` anchors it to real time. The
     * server stamps its own arrival time on top.
     */
    observed_at_ms: z.number().int().nonnegative(),
    frame_start: z.number().int().nonnegative(),
    frame_end: z.number().int().nonnegative(),
    suggested_foul_points: z.number().int().min(MIN_FOUL_POINTS).max(MAX_FOUL_POINTS).nullish(),
    suggested_offender_plays_again: z.boolean().nullish(),
    evidence: AiEvidenceSchema,
});
/**
 * A candidate as it arrives on the wire.
 *
 * The refinements are the rules the recogniser is allowed to be held to —
 * things about *balls*, which it can see. Anything requiring the score, the
 * turn or the frame is checked by the server at acceptance, not here.
 */
export const AiCandidateSchema = candidateWire
    .refine((c) => c.frame_end >= c.frame_start, {
    message: 'frame_end must not precede frame_start',
    path: ['frame_end'],
})
    .refine((c) => (c.count ?? 1) === 1 || c.color === 'red', {
    message: 'only reds can be potted more than one at a time',
    path: ['count'],
})
    .refine((c) => c.kind !== 'ball_potted' || (c.color != null && c.color !== 'white'), {
    message: 'ball_potted needs a scoring colour; the cue ball is never a pot',
    path: ['color'],
})
    .refine((c) => c.kind !== 'cue_ball_potted_foul' || c.color == null || c.color === 'white', {
    message: 'cue_ball_potted_foul describes the cue ball',
    path: ['color'],
})
    .refine((c) => c.suggested_foul_points == null || c.kind === 'cue_ball_potted_foul', { message: 'only a foul candidate may suggest foul points', path: ['suggested_foul_points'] })
    .transform((c) => ({
    candidateId: c.candidate_id,
    runId: c.run_id,
    tableId: c.table_id,
    streamId: c.stream_id,
    segmentId: c.segment_id,
    eventGroupId: c.event_group_id,
    kind: c.kind,
    color: c.color ?? null,
    count: c.count ?? null,
    confidence: c.confidence,
    observedAtMs: c.observed_at_ms,
    frameStart: c.frame_start,
    frameEnd: c.frame_end,
    suggestedFoulPoints: c.suggested_foul_points ?? null,
    suggestedOffenderPlaysAgain: c.suggested_offender_plays_again ?? null,
    evidence: c.evidence,
}));
/**
 * Candidates arrive in batches because shots arrive in batches: the worker
 * spools while the network is down and sends what it has when it comes back.
 * The cap is there so a spool that ran all night cannot arrive as one request.
 */
export const AiCandidateBatchSchema = z.object({
    protocolVersion: z.number().int().positive().default(AI_PROTOCOL_VERSION),
    candidates: z.array(AiCandidateSchema).min(1).max(50),
});
/**
 * A worker announcing itself.
 *
 * The model version and the plan hash are here because the first question
 * about a bad call is always "which model was that?", and the answer has to
 * survive in the record next to the candidate it explains.
 *
 * `started_at` is the anchor: it is the only wall-clock time the worker sends,
 * and every `observed_at_ms` in the run hangs off it.
 */
export const AiRunSchema = z
    .object({
    protocol_version: z.number().int().positive().default(AI_PROTOCOL_VERSION),
    run_id: z.string().min(1).max(200),
    table_id: z.string().min(1).max(200),
    stream_id: z.string().min(1).max(200),
    /** ISO 8601, from the worker's clock, however bad it is. */
    started_at: z.string().datetime(),
    /** Stream position at `started_at`; non-zero when replaying from a seek. */
    stream_epoch_ms: z.number().int().nonnegative().default(0),
    model_name: z.string().max(200).nullish(),
    model_version: z.string().max(200).nullish(),
    /** TensorRT plan hash: the same weights built twice are not the same. */
    plan_hash: z.string().max(200).nullish(),
    config: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).default({}),
})
    .transform((r) => ({
    protocolVersion: r.protocol_version,
    runId: r.run_id,
    tableId: r.table_id,
    streamId: r.stream_id,
    startedAt: r.started_at,
    streamEpochMs: r.stream_epoch_ms,
    modelName: r.model_name ?? null,
    modelVersion: r.model_version ?? null,
    planHash: r.plan_hash ?? null,
    config: r.config,
}));
/**
 * How the recogniser is doing.
 *
 * `degraded` is the important one, and it is why the heartbeat exists at all.
 * Silence from the worker and a table where genuinely nothing happened look
 * identical from the scoreboard, and an operator who assumes the first when it
 * is the second stops watching. A worker that has hit its memory ceiling, lost
 * the stream or filled its spool says so, and the board says so too.
 */
export const AiWorkerStatusSchema = z.enum(['online', 'degraded', 'offline']);
export const AiHeartbeatSchema = z
    .object({
    run_id: z.string().min(1).max(200),
    status: AiWorkerStatusSchema,
    /** Free text for the board, e.g. `spool full`, `stream lost`. */
    detail: z.string().max(200).nullish(),
    /** Stream position now, so a stalled pipeline is visible as a flat line. */
    observed_at_ms: z.number().int().nonnegative().default(0),
    pending_candidates: z.number().int().nonnegative().default(0),
    dropped_candidates: z.number().int().nonnegative().default(0),
})
    .transform((h) => ({
    runId: h.run_id,
    status: h.status,
    detail: h.detail ?? null,
    observedAtMs: h.observed_at_ms,
    pendingCandidates: h.pending_candidates,
    droppedCandidates: h.dropped_candidates,
}));
/**
 * Where a candidate is in its life.
 *
 * `conflict` is separate from `rejected` on purpose. Rejected means a human
 * looked and said no, which is training data. Conflict means the match moved
 * on before anyone looked, which is not the recogniser's fault and must not be
 * counted against it.
 */
export const AiCandidateStatusSchema = z.enum([
    'pending',
    'accepted',
    'rejected',
    'expired',
    'conflict',
]);
/**
 * Accepting a candidate.
 *
 * `expectedSeq` is what the reviewing client had on screen. If the log has
 * moved on the server answers `409` and the candidate becomes `conflict`,
 * rather than applying a pot to a frame that has since restarted. The client
 * must send it: an accept without one is a click made against a screen that
 * may no longer exist.
 */
export const AcceptAiCandidateSchema = z.object({
    expectedSeq: z.number().int().nonnegative(),
    /**
     * Overrides for what the recogniser suggested. The operator watched the
     * shot; the camera only counted the balls afterwards.
     */
    foulPoints: z.number().int().min(MIN_FOUL_POINTS).max(MAX_FOUL_POINTS).optional(),
    offenderPlaysAgain: z.boolean().optional(),
    /** Normally the server binds the player at `observedSeq`; this overrules. */
    player: PlayerIndexSchema.optional(),
});
/**
 * Rejecting one.
 *
 * The reason is required and enumerated, because free text is never analysed
 * and "wrong colour" versus "nothing happened" are the difference between
 * retraining the classifier and retuning the segmenter. Rejection records the
 * refusal only: whatever really happened is entered afterwards through the
 * ordinary manual event route, as `source: manual`, so that the score has one
 * way in and one way only.
 */
export const AiRejectionReasonSchema = z.enum([
    'wrong_colour',
    'wrong_count',
    'wrong_event_type',
    'nothing_happened',
    'already_recorded',
    'too_late',
    'other',
]);
export const RejectAiCandidateSchema = z.object({
    reason: AiRejectionReasonSchema,
    note: z.string().max(500).optional(),
});
/**
 * Ingest is addressed by *table*, review by *match*.
 *
 * This is the correction that matters most for deployment. A camera is bolted
 * above a table and knows only which table it is; the match on that table
 * changes several times an evening. Match-scoped ingest would mean
 * reconfiguring and restarting the worker between games, which is exactly the
 * manual step the whole system exists to remove. The server already knows the
 * binding — devices carry `matchId` — so it resolves it, and a candidate that
 * arrives for an unbound table is stored against no match and never scores.
 *
 * The worker authenticates with its own table-scoped token. It is emphatically
 * not the camera's device token: that one can rewrite the stream key, and a
 * process doing GPU inference on a public video feed is not where that
 * capability belongs.
 */
export const AI_API = {
    /** Announce a run. Idempotent on `run_id`. */
    runs: (tableId) => `/api/tables/${tableId}/ai/runs`,
    /** Submit candidates. Idempotent on `candidate_id`. */
    candidates: (tableId) => `/api/tables/${tableId}/ai/candidates`,
    heartbeat: (tableId) => `/api/tables/${tableId}/ai/heartbeat`,
    /** Review, by match, because that is what an operator is looking at. */
    review: (matchId) => `/api/matches/${matchId}/ai/candidates`,
    accept: (matchId, candidateId) => `/api/matches/${matchId}/ai/candidates/${encodeURIComponent(candidateId)}/accept`,
    reject: (matchId, candidateId) => `/api/matches/${matchId}/ai/candidates/${encodeURIComponent(candidateId)}/reject`,
    status: (matchId) => `/api/matches/${matchId}/ai/status`,
};
/** Header the AI worker authenticates with, as `Authorization: ****** */
export const AI_AUTH_SCHEME = 'Bearer';
/**
 * How an accepted candidate becomes an event.
 *
 * Two of the six map to nothing at all, and that is the point: a ball waiting
 * for its spot and a ball put back are table state. Accepting them records
 * that a human agreed with the camera and changes no score. `uncertain` has no
 * mapping either — accepting it only means "yes, something happened", and the
 * operator still has to say what.
 */
export const AI_CANDIDATE_EVENT_TYPE = {
    ball_potted: 'shot.pot',
    cue_ball_potted_foul: 'shot.foul',
    turn_ended_no_score: 'shot.miss',
    ball_waiting_for_spot: null,
    ball_respotted: null,
    uncertain: null,
};
/**
 * The `clientId` an accepted candidate writes into the log.
 *
 * Prefixed so that a glance at the event tells you a machine proposed it, and
 * so that the recogniser can never collide with a client id typed by a human
 * or generated by the phone app.
 */
export function aiClientId(candidateId) {
    return `ai:${candidateId}`;
}
//# sourceMappingURL=ai.js.map